/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable max-lines-per-function */
/* eslint-disable id-length */
import React, { useEffect, useState, useCallback } from 'react'
import { Grid, Tooltip } from '@mui/material'
import Layout from '~/components/Layout'
import { useLang } from '~/hooks/useLang'
import InternalHeader from '~/components/Layout/components/InternalHeader'
import InternalContent from '~/components/Layout/components/InternalContent'
import Wordcloud from '@visx/wordcloud/lib/Wordcloud'
import { scaleLog } from '@visx/scale'
import { Text } from '@visx/text'
import { useTheme } from '~/hooks/useTheme'
import useSystemState from '~/features/System/stores/SystemState'
import useWordCloudState, {
  type IResponseWords,
} from '../stores/WordCloudState'
import RefreshIcon from '@mui/icons-material/Refresh'
import ButtonRefresh from '../components/SearchWordCloudModalDraggable/components/ButtonRefresh'

interface WordData {
  readonly text: string
  readonly value: number
}

export default function WordCloud() {
  const { translate } = useLang()
  const { darkMode } = useTheme()
  const { windowManager, windowIsVisible } = useSystemState()
  const { wordCloud, getWords } = useWordCloudState()
  const [dataWords, setDataWords] = useState<WordData[]>([])
  const [hoveredWord, setHoveredWord] = useState<string | null>(null)
  const colors = ['#00c582', '#00ae4d', '#1f9700', '#038C7F']
  const [width, setWidth] = useState<number | null>(null)
  const fixedValueGenerator = () => 0.5
  const mokedData: WordData[] = [
    {
      text: 'clinica',
      value: 9.687170014926268,
    },
    {
      text: 'faixa',
      value: 9.687170014926268,
    },
    {
      text: 'trafico',
      value: 9.687170014926268,
    },
    {
      text: 'st',
      value: 3.294955787389887,
    },
    {
      text: 'josefa',
      value: 3.294955787389887,
    },
    {
      text: 'drogas',
      value: 2.296121256138144,
    },
    {
      text: 'empresa',
      value: 2.032661638573906,
    },
  ]
  const div = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      setWidth(node.getBoundingClientRect().width)
    }
  }, [])

  const getWordsFromState = async () => {
    try {
      const responseGetWords: IResponseWords[] = await getWords()

      const normalizedWords: WordData[] = responseGetWords.map((item) => {
        return { text: item.word, value: item.weight }
      })

      setDataWords(normalizedWords)
    } catch (error) {
      console.error('Error fetching word cloud:', error)
    }
  }

  useEffect(() => {
    document.title = translate('Word Cloud - Dispatch System')
  }, [])

  useEffect(() => {
    windowIsVisible(['searchWordCloud'])

    getWordsFromState()
  }, [])

  const minWordValue =
    dataWords.length > 0 ? Math.min(...dataWords.map((word) => word.value)) : 1
  const maxWordValue =
    dataWords.length > 0 ? Math.max(...dataWords.map((word) => word.value)) : 1

  const fontScale = scaleLog({
    domain: [minWordValue, maxWordValue],
    range: [dataWords.length <= 15 ? 50 : 10, 100],
  })

  const fontSizeSetter = (datum: WordData) => fontScale(datum.value)

  const handleOpenResultSearchWord = (word: string) => {
    wordCloud().set(word)

    if (!windowManager()['searchWordCloud'].open.get()) {
      windowManager()['searchWordCloud'].open.set(true)
    }
  }

  return (
    <Layout marginLayout={true} draggableComponents={true}>
      <InternalHeader
        title={translate('Word Cloud')}
        action={
          <Tooltip title={translate('Refresh')} arrow>
            <ButtonRefresh title={translate('Refresh')}>
              <RefreshIcon
                onClick={async () => {
                  await getWordsFromState()
                }}
              />
            </ButtonRefresh>
          </Tooltip>
        }
      />
      <InternalContent expandButton={false}>
        <Grid container sx={{ justifyContent: 'center' }}>
          {mokedData.length > 0 && (
            <Grid
              item
              xs={12}
              ref={div}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Wordcloud
                words={mokedData}
                width={width ?? 600}
                height={750}
                fontSize={fontSizeSetter}
                font={'Impact'}
                padding={5}
                spiral={'archimedean'}
                rotate={0}
                random={fixedValueGenerator}
              >
                {(cloudWords) =>
                  cloudWords.map((word, index) => (
                    <g
                      key={word.text}
                      onMouseEnter={() => setHoveredWord(String(word.text))}
                      onMouseLeave={() => setHoveredWord(null)}
                      style={{
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease',
                        transformOrigin: `${word.x}px ${word.y}px`,
                        transform:
                          hoveredWord === word.text
                            ? 'scale(1.2)'
                            : 'scale(1)',
                      }}
                    >
                      <title>
                        {translate('Click to see occurrences of') +
                          ` "${word.text}"`}
                      </title>
                      <Text
                        fill={colors[index % colors.length]}
                        textAnchor={'middle'}
                        transform={`translate(${word.x}, ${word.y}) rotate(${word.rotate})`}
                        fontFamily={word.font}
                        fontSize={word.size}
                        onClick={() =>
                          handleOpenResultSearchWord(String(word.text))
                        }
                        style={{
                          transition: 'fill 0.2s ease',
                        }}
                      >
                        {word.text}
                      </Text>
                    </g>
                  ))
                }
              </Wordcloud>
            </Grid>
          )}
        </Grid>
      </InternalContent>
    </Layout>
  )
}
